import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/auth/useAuth';

interface SkillAssessmentFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const SKILL_CATEGORIES = {
  'Technical': [
    'JavaScript',
    'Python',
    'React',
    'Node.js',
    'SQL',
    'Machine Learning',
    'Data Analysis',
    'Cloud Computing',
    'DevOps',
    'System Design'
  ],
  'Business': [
    'Product Management',
    'Project Management',
    'Business Analysis',
    'Digital Marketing',
    'Content Strategy',
    'Financial Analysis',
    'Sales',
    'Market Research'
  ],
  'Soft Skills': [
    'Leadership',
    'Communication',
    'Problem Solving',
    'Team Management',
    'Negotiation',
    'Public Speaking',
    'Critical Thinking',
    'Time Management'
  ]
};

const PROFICIENCY_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' }
];

export function SkillAssessmentForm({ onClose, onSuccess }: SkillAssessmentFormProps) {
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof SKILL_CATEGORIES>('Technical');
  const [selectedSkills, setSelectedSkills] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  // Function to save skills data
  async function handleSave() {
    if (Object.keys(selectedSkills).length === 0) {
      toast.error('Please select at least one skill');
      return;
    }

    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Use correct table and column names from your SQL schema
      const skillAssessments = Object.entries(selectedSkills).map(([skill, level]) => ({
        user_id: user.id,
        skill_name: skill,
        proficiency_level: level, // Using proficiency_level instead of level
        last_assessed: new Date().toISOString() // Using last_assessed instead of last_updated
      }));

      // Insert or update skills in the correct table
      const { error } = await supabase
        .from('user_skill_assessments') // Using the correct table name
        .upsert(skillAssessments, { onConflict: 'user_id,skill_name' });

      if (error) {
        console.error('Error saving skills:', error);
        throw error;
      }
      
      // Success!
      toast.success('Skills updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save skills:', error);
      toast.error('Failed to save skills');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-black">Assess Your Skills</h2>
            <button 
              type="button"
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.keys(SKILL_CATEGORIES).map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category as keyof typeof SKILL_CATEGORIES)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#800020] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SKILL_CATEGORIES[selectedCategory].map((skill) => (
                <div key={skill} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-black">
                      {skill}
                    </label>
                    <select
                      value={selectedSkills[skill] || ''}
                      onChange={(e) => setSelectedSkills(prev => ({
                        ...prev,
                        [skill]: e.target.value
                      }))}
                      className="text-sm bg-white border border-gray-200 rounded-lg px-2 py-1 text-black"
                    >
                      <option value="">Select level</option>
                      {PROFICIENCY_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedSkills[skill] && (
                    <div className="mt-2">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[#800020] h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${
                              selectedSkills[skill] === 'beginner' ? '25%' :
                              selectedSkills[skill] === 'intermediate' ? '50%' :
                              selectedSkills[skill] === 'advanced' ? '75%' :
                              '100%'
                            }`
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Skills'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}